import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Settings, Loader2 } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Les seuils sont stockés en ratio (0.15) et saisis en pourcentage (15).
const FIELDS = [
  { key: 'laborSellPrice', label: "Prix de vente main d'œuvre", unit: '€/h', kind: 'number',
    hint: "Estime les heures d'un devis et l'écart d'heures des chantiers." },
  { key: 'avgHourlyCost', label: 'Coût horaire moyen salarié', unit: '€/h', kind: 'number',
    hint: 'Sert à la marge estimée des chantiers.' },
  { key: 'overrunAlertThreshold', label: "Seuil d'alerte dépassement d'heures", unit: '%', kind: 'percent',
    hint: 'Au-delà de cet écart, le chantier est signalé en dépassement.' },
  { key: 'unproductiveHourlyCost', label: 'Coût horaire improductif', unit: '€/h', kind: 'number',
    hint: "Informatif : aucun calcul ne l'utilise aujourd'hui." },
  { key: 'lowMarginThreshold', label: 'Seuil marge faible', unit: '%', kind: 'percent',
    hint: "Informatif : aucun calcul ne l'utilise aujourd'hui." },
  { key: 'mainManager', label: 'Responsable principal', kind: 'text' },
  { key: 'companyName', label: "Nom de l'entreprise", kind: 'text' },
  { key: 'companyAddress', label: 'Adresse de la société', kind: 'text',
    hint: "Point de départ du calcul automatique de la zone de trajet (prime) de chaque chantier, d'après son adresse." },
  { key: 'employeeStatsFrom', label: 'Stats salariés : à partir de', kind: 'month',
    hint: "Les mois d'avant sont écartés du tableau « Contribution par salarié » — pointages pas encore fiables. Vide = depuis le premier mois pointé." },
];

const toForm = (settings) => Object.fromEntries(FIELDS.map(({ key, kind }) => {
  const value = settings?.[key];
  if (value === null || value === undefined) return [key, ''];
  if (kind === 'percent') return [key, String(Math.round(value * 10000) / 100)];
  return [key, String(value)];
}));

const fromForm = (form) => Object.fromEntries(FIELDS.map(({ key, kind }) => {
  const raw = (form[key] ?? '').toString().trim();
  if (kind === 'text') return [key, raw];
  if (kind === 'month') return [key, raw || null];
  const number = parseFloat(raw.replace(',', '.'));
  return [key, kind === 'percent' ? number / 100 : number];
}));

const SettingsDialog = ({ isOpen, onOpenChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(toForm({}));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    settingsAPI.get()
      .then((settings) => setForm(toForm(settings)))
      .catch(() => toast({
        title: 'Erreur',
        description: 'Impossible de charger les paramètres',
        variant: 'destructive',
      }))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [isOpen]);

  const numbersValid = FIELDS
    .filter(({ kind }) => kind !== 'text' && kind !== 'month')
    .every(({ key }) => !Number.isNaN(parseFloat((form[key] ?? '').toString().replace(',', '.'))));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await settingsAPI.update(fromForm(form));
      setForm(toForm(result.settings));
      toast({ title: 'Paramètres enregistrés', duration: 3000 });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Échec de l'enregistrement",
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white border border-gray-200 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-gray-800">
            <Settings className="w-5 h-5 mr-2" />
            Paramètres
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-gray-500 flex items-center">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...
          </p>
        ) : (
          <div className="space-y-4">
            {FIELDS.map(({ key, label, unit, kind, hint }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`settings-${key}`} className="text-gray-700">
                  {label}{unit ? ` (${unit})` : ''}
                </Label>
                <Input
                  id={`settings-${key}`}
                  type={kind === 'text' ? 'text' : kind === 'month' ? 'month' : 'number'}
                  step={kind === 'number' || kind === 'percent' ? '0.01' : undefined}
                  min={kind === 'number' || kind === 'percent' ? '0' : undefined}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="border-gray-200"
                />
                {hint && <p className="text-xs text-gray-500">{hint}</p>}
              </div>
            ))}
            <Button
              onClick={handleSave}
              disabled={!numbersValid || isSaving}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white"
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
